import { useState } from 'react'
import { toast } from 'sonner'

import { GoogleOauth } from '@/components/GoogleOauth'
import { DefaultVendorToggle } from '@/components/Telephony/DefaultVendorToggle'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Field, FieldGroup } from '@/components/ui/field'
import { Label } from '@/components/ui/label'
import { MarkdownEditor } from '@/components/ui/markdown-editor'
import { parseError } from '@/lib/errors'
import { useDisconnectGoogle, type User, useUpdateHubPreferences } from '@/models/user'
import { BrokerPhotoCard } from './BrokerPhotoCard'

export function StaffTab({ user }: { user: User }) {
  const prefs = user.hubPreferences
  const [signature, setSignature] = useState(prefs.emailSignature)
  const [editorKey, setEditorKey] = useState(0)
  const update = useUpdateHubPreferences()
  const disconnect = useDisconnectGoogle()

  const dirty = signature !== prefs.emailSignature

  function handleSave() {
    update.mutate(
      { emailSignature: signature },
      {
        onSuccess: () => toast.success('Signature saved'),
        onError: (err) => toast.error(parseError(err)),
      }
    )
  }

  function handleReset() {
    setSignature(prefs.emailSignature)
    setEditorKey((k) => k + 1)
  }

  function handleDisconnect() {
    disconnect.mutate(undefined, {
      onSuccess: () => toast.success('Disconnected from Google'),
      onError: (err) => toast.error(parseError(err)),
    })
  }

  return (
    <>
      <Card>
        <CardHeader>
          <CardTitle>Email Signature</CardTitle>
          <CardDescription>
            Appended to outbound sales emails after "Thanks,". Markdown supported.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <GoogleOauth connectLabel="Connect Google to manage your signature">
            <FieldGroup className="max-w-2xl">
              <Field>
                <Label>Signature</Label>
                <MarkdownEditor
                  key={editorKey}
                  value={signature}
                  onChange={setSignature}
                  placeholder={'Your Name\n📱 555-1234'}
                  compact
                />
              </Field>
              <div className="flex gap-2">
                <Button
                  onClick={handleSave}
                  disabled={!dirty || update.isPending}
                  loading={update.isPending ? 'Saving…' : undefined}
                >
                  Save
                </Button>
                {dirty && (
                  <Button variant="ghost" onClick={handleReset} disabled={update.isPending}>
                    Reset
                  </Button>
                )}
              </div>
            </FieldGroup>
          </GoogleOauth>
          {user.googleMailConnected && (
            <div className="mt-6 flex max-w-2xl items-center justify-between border-t pt-4">
              <p className="text-muted-foreground text-sm">
                Sending sales email from{' '}
                <span className="text-foreground font-medium">{user.googleMailEmail}</span>.
              </p>
              <Button
                variant="outline"
                onClick={handleDisconnect}
                disabled={disconnect.isPending}
                loading={disconnect.isPending ? 'Disconnecting…' : undefined}
              >
                Disconnect Google
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      <BrokerPhotoCard user={user} />

      <Card>
        <CardHeader>
          <CardTitle>Telephony</CardTitle>
          <CardDescription>
            Choose which telephony provider to use when sending SMS messages and making calls.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <DefaultVendorToggle />
        </CardContent>
      </Card>
    </>
  )
}
